/**
 * Utility functions for category and subcategory name resolution
 */

export interface CategoryInfo {
  categoryId: string;
  categoryName: string;
}

export interface SubcategoryInfo {
  subCategoryId: string;
  subCategoryName: string;
  categoryId?: string;
}

export class CategoryUtils {
  /**
   * Resolve app category and subcategory names from UUIDs
   * If already names (not UUIDs), returns as-is for backward compatibility
   */
  static resolveAppCategoryNames(
    app: any, 
    categories: CategoryInfo[], 
    subcategories: SubcategoryInfo[]
  ): { categoryName: string; subcategoryName: string } {
    let categoryName = app.category || '';
    let subcategoryName = app.subCategory || '';

    // Check if category is UUID (contains dashes) and resolve it
    if (categoryName.includes('-') && categories) {
      const foundCategory = categories.find(cat => cat.categoryId === categoryName);
      if (foundCategory) {
        categoryName = foundCategory.categoryName;
      }
    }

    // Check if subcategory is UUID (contains dashes) and resolve it  
    if (subcategoryName.includes('-') && subcategories) {
      const foundSubcategory = subcategories.find(sub => sub.subCategoryId === subcategoryName);
      if (foundSubcategory) {
        subcategoryName = foundSubcategory.subCategoryName;
      }
    }

    return {
      categoryName: categoryName || 'Unknown Category',
      subcategoryName: subcategoryName || 'Unknown Subcategory'
    };
  }

  /**
   * Check if a string looks like a UUID
   */
  static isUUID(str: string): boolean {
    if (!str) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }

  /**
   * Get display name for category (resolve UUID if needed)
   */
  static getCategoryDisplayName(categoryId: string, categories: CategoryInfo[]): string {
    if (!categoryId) return 'Unknown Category';
    
    // If it's already a name (not UUID), return as-is
    if (!this.isUUID(categoryId)) {
      return categoryId;
    }

    // Look up by UUID
    const found = categories?.find(cat => cat.categoryId === categoryId);
    return found ? found.categoryName : 'Unknown Category';
  }

  /**
   * Get display name for subcategory (resolve UUID if needed)
   */
  static getSubcategoryDisplayName(subcategoryId: string, subcategories: SubcategoryInfo[]): string {
    if (!subcategoryId) return 'Unknown Subcategory';
    
    // If it's already a name (not UUID), return as-is
    if (!this.isUUID(subcategoryId)) {
      return subcategoryId;
    }

    // Look up by UUID
    const found = subcategories?.find(sub => sub.subCategoryId === subcategoryId);
    return found ? found.subCategoryName : 'Unknown Subcategory';
  }
}